export class ApiError extends Error {
    status;
    code;
    fields;
    incidentId;
    conflict;
    locked;
    constructor(status, code, message, 
    /** Feldbezogene Befunde; die API deckelt sie (höchstens 50, je höchstens 300 Zeichen). */
    fields, incidentId, cause, 
    /** Nur bei 409 gefüllt: der aktuelle Stand des Datensatzes auf dem Server. */
    conflict, 
    /** Nur bei 423 gefüllt: wer die Sperre hält und seit wann. */
    locked) {
        super(message, cause !== undefined ? { cause } : undefined);
        this.status = status;
        this.code = code;
        this.fields = fields;
        this.incidentId = incidentId;
        this.conflict = conflict;
        this.locked = locked;
        this.name = "ApiError";
    }
}
/**
 * Gemeinsame Fehlerauswertung für hole(), sende() und sendeUndLies(). Bis Aufgabe 12 stand
 * dieselbe Auswertung zweimal wortgleich in hole() und sende() — eine bewusste, terminierte
 * Doppelung aus Aufgabe 2 (siehe dortige Entscheidung), jetzt hier zusammengezogen.
 */
async function wirfBeiFehler(antwort) {
    if (antwort.ok)
        return;
    let koerper = null;
    try {
        koerper = (await antwort.json());
    }
    catch { /* kein JSON */ }
    const konflikt = antwort.status === 409 && koerper?.changedBy !== undefined
        ? { changedBy: koerper.changedBy, changedAt: koerper.changedAt ?? "", current: koerper.current }
        : undefined;
    const gesperrt = antwort.status === 423 && koerper?.lockedBy !== undefined
        ? { lockedBy: koerper.lockedBy, since: koerper.since ?? "" }
        : undefined;
    throw new ApiError(antwort.status, koerper?.code ?? "UNBEKANNT", koerper?.message ?? `Die Anfrage schlug fehl (${antwort.status}).`, koerper?.fields, koerper?.incidentId, undefined, konflikt, gesperrt);
}
async function hole(basis, pfad) {
    let antwort;
    try {
        antwort = await fetch(`${basis}${pfad}`, {
            credentials: "include",
            headers: { accept: "application/json" },
        });
    }
    catch (ursprung) {
        // fetch selbst wirft z. B. bei Verbindungsverweigerung oder fehlendem Netz —
        // das darf die Oberfläche nicht als rohen TypeError zu sehen bekommen.
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    try {
        return (await antwort.json());
    }
    catch (ursprung) {
        // Eine 200er-Antwort mit kaputtem Inhalt (z. B. durch einen fehlerhaften
        // Zwischenserver) soll ebenfalls als ApiError ankommen, nicht als roher SyntaxError.
        throw new ApiError(antwort.status, "UNGUELTIGES_JSON", "Die Antwort des Servers konnte nicht gelesen werden.", undefined, undefined, ursprung);
    }
}
/**
 * Die Anfrage-Vorgaben für einen Schreibweg — an EINER Stelle für `sende` und `sendeUndLies`.
 *
 * **Ohne Rumpf kein `content-type`.** Das ist keine Kosmetik: Fastify 5 weist ein DELETE mit
 * `content-type: application/json`, aber leerem Rumpf mit 400 ab
 * (`FST_ERR_CTP_EMPTY_JSON_BODY`) — noch vor dem Handler. Bis Befund W1 der Prüfung von
 * Aufgabe 3 setzten beide Funktionen den Kopf pauschal, und damit funktionierte KEIN einziger
 * Löschweg dieses Clients gegen einen echten Server (`deleteNote`, `deleteImportantNote`,
 * `deleteFile`, `deleteFolder`, `deleteTextBlock`, `deleteOfferTemplate`, `deleteItem`).
 * Gemerkt hat es niemand, weil die GESTALT-Tabelle `fetch` fälscht — die Gegenproben stehen
 * deshalb in `client.test.ts` an einem ECHTEN Fastify über eine echte Leitung.
 *
 * `JSON.stringify(undefined)` ist `undefined`; `fetch` sendet dann keinen Rumpf. Ein Aufrufer,
 * der wirklich `null` schicken will, übergibt `null` — das ist gültiges JSON und bekommt
 * folgerichtig auch den Kopf.
 */
function schreibVorgaben(methode, koerper) {
    const rumpf = koerper === undefined ? undefined : JSON.stringify(koerper);
    return {
        method: methode,
        credentials: "include",
        headers: rumpf === undefined
            ? { accept: "application/json" }
            : { accept: "application/json", "content-type": "application/json" },
        ...(rumpf === undefined ? {} : { body: rumpf }),
    };
}
async function sende(basis, pfad, methode, koerper) {
    let antwort;
    try {
        antwort = await fetch(`${basis}${pfad}`, schreibVorgaben(methode, koerper));
    }
    catch (ursprung) {
        // Dieselbe Behandlung wie bei hole(): fetch selbst wirft z. B. bei
        // Verbindungsverweigerung oder fehlendem Netz.
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
}
/**
 * Wie sende(), aber für Schreibwege MIT Antwortkörper (Anlegen, Ändern, Sperre erwerben/
 * erneuern, Position löschen). Dieselbe Netzwerk- und Fehlerbehandlung wie hole() und sende()
 * — und dieselben Anfrage-Vorgaben, also auch hier ohne Rumpf kein `content-type`; zusätzlich
 * wird die erfolgreiche Antwort gelesen, mit derselben Absicherung gegen kaputtes JSON.
 */
/**
 * Wie `sendeUndLies`, gibt aber den ANTWORTCODE mit zurück.
 *
 * Gebraucht an genau einem Weg: „Korrekturrechnung erstellen" antwortet mit 201, wenn sie
 * eben entstanden ist, und mit 200, wenn es sie schon gab (`korrektur-erzeugen`). Beide
 * Male steht dieselbe Rechnung im Rumpf — der Unterschied steckt AUSSCHLIESSLICH im Code,
 * und ohne ihn müsste die Maske ihn aus den Daten erraten.
 *
 * `sendeUndLies` bleibt die Vorgabe für alle übrigen Wege und ruft diese Funktion auf: Zwei
 * Fassungen derselben Fehlerauswertung liefen unweigerlich auseinander.
 */
async function sendeUndLiesMitCode(basis, pfad, methode, koerper) {
    let antwort;
    try {
        antwort = await fetch(`${basis}${pfad}`, schreibVorgaben(methode, koerper));
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    try {
        return { status: antwort.status, daten: (await antwort.json()) };
    }
    catch (ursprung) {
        throw new ApiError(antwort.status, "UNGUELTIGES_JSON", "Die Antwort des Servers konnte nicht gelesen werden.", undefined, undefined, ursprung);
    }
}
async function sendeUndLies(basis, pfad, methode, koerper) {
    // Der Antwortcode interessiert hier nicht — nur ein Weg unterscheidet 200 von 201
    // (siehe `sendeUndLiesMitCode`), und die Fehlerauswertung soll es nur einmal geben.
    return (await sendeUndLiesMitCode(basis, pfad, methode, koerper)).daten;
}
/**
 * Wie sendeUndLies(), aber für einen Datei-Upload als multipart/form-data. Bewusst OHNE einen
 * eigenen content-type-Kopf: Bei einem FormData-Rumpf setzt fetch den Kopf selbst — samt der
 * `boundary`, die den Rumpf strukturiert. Ein von Hand gesetzter content-type (wie bei den
 * JSON-Methoden) verlöre die boundary und machte den Rumpf unlesbar. Sonst dieselbe
 * Netzwerk- und Fehlerbehandlung.
 */
async function sendeFormular(basis, pfad, form, methode = "POST") {
    let antwort;
    try {
        antwort = await fetch(`${basis}${pfad}`, {
            // PUT seit der Unterschrift am Benutzer, Layout-Editor Etappe 1.
            method: methode,
            credentials: "include",
            headers: { accept: "application/json" },
            body: form,
        });
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    try {
        return (await antwort.json());
    }
    catch (ursprung) {
        throw new ApiError(antwort.status, "UNGUELTIGES_JSON", "Die Antwort des Servers konnte nicht gelesen werden.", undefined, undefined, ursprung);
    }
}
/** Zieht den Dateinamen aus einem Content-Disposition-Kopf — bevorzugt den verlustfreien
 *  RFC-5987-Teil (`filename*=UTF-8''…`), sonst den ASCII-Rückfall (`filename="…"`). `null`,
 *  wenn keiner da ist. */
function dateinameAusKopf(kopf) {
    if (kopf === null)
        return null;
    const stern = /filename\*=UTF-8''([^;]+)/i.exec(kopf);
    if (stern !== null) {
        try {
            return decodeURIComponent(stern[1]);
        }
        catch { /* fällt auf ascii zurück */ }
    }
    const ascii = /filename="([^"]*)"/i.exec(kopf);
    return ascii !== null ? ascii[1] : null;
}
/**
 * Holt eine Binärdatei (Download) mit derselben Netzwerk-/Fehlerbehandlung wie hole(), gibt aber
 * die rohen Bytes als Blob zurück — plus den ausgelieferten Dateinamen aus dem
 * Content-Disposition-Kopf (den die Route IMMER als attachment setzt). Ohne accept:json, weil
 * die Antwort kein JSON ist.
 */
async function holeBinaer(basis, pfad) {
    let antwort;
    try {
        antwort = await fetch(`${basis}${pfad}`, { credentials: "include" });
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    const blob = await antwort.blob();
    return { blob, filename: dateinameAusKopf(antwort.headers.get("content-disposition")) };
}
/**
 * Wie holeBinaer(), aber als rumpfloses POST — die Rendern-Wege (E3-T4) LÖSEN etwas aus
 * (die Engine setzt den Beleg) und sind deshalb POSTs, ihre Antwort ist aber derselbe
 * Download wie bei einer Datei. Ohne Rumpf KEIN content-type (Befund W1: Fastify 5 weist
 * ein leeres JSON-POST mit gesetztem Kopf ab) und ohne accept:json — die Antwort ist PDF.
 */
async function sendeBinaer(basis, pfad) {
    let antwort;
    try {
        antwort = await fetch(`${basis}${pfad}`, { method: "POST", credentials: "include" });
    }
    catch (ursprung) {
        throw new ApiError(0, "NETZWERKFEHLER", "Der Server ist nicht erreichbar. Bitte Internetverbindung prüfen.", undefined, undefined, ursprung);
    }
    await wirfBeiFehler(antwort);
    const blob = await antwort.blob();
    return { blob, filename: dateinameAusKopf(antwort.headers.get("content-disposition")) };
}
/** Der Verwaltungspfad einer Logo-Variante (Hochladen und Löschen) — an einer Stelle,
 *  damit die beiden Methoden nicht auseinanderlaufen können. Die Variante ist getippt und
 *  braucht keine Kodierung; die Kennung schon. */
function verwaltungsLogoPfad(id, variante) {
    return `/api/verwaltung/unternehmen/${encodeURIComponent(id)}/logo/${variante}`;
}
/** Baut die Abfragezeichenkette der Rechnungsliste. Eigene Funktion und nicht
 *  `vorgangsSucheAnhang` mit einem Sonderzweig: Der Statusfilter ist hier WIEDERHOLBAR
 *  (`?status=ISSUED&status=PAID`), und ein Helfer, der beides kann, könnte beides auch
 *  verwechseln. */
function rechnungsSucheAnhang(q) {
    const p = new URLSearchParams();
    if (q.companyId !== undefined && q.companyId !== "")
        p.set("companyId", q.companyId);
    for (const s of q.status ?? [])
        p.append("status", s);
    if (q.suche !== undefined && q.suche !== "")
        p.set("suche", q.suche);
    if (q.limit !== undefined)
        p.set("limit", String(q.limit));
    if (q.offset !== undefined)
        p.set("offset", String(q.offset));
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Die Abfragezeichenkette der Klärliste — nur GESETZTE Angaben landen darin (Bauart wie
 *  `rechnungsSucheAnhang`). `alle: false` wird weggelassen: Es ist die Vorgabe des Servers,
 *  und eine Zeichenkette, die Vorgaben wiederholt, veraltet mit der ersten Änderung. */
function klaerlisteAnhang(q) {
    const p = new URLSearchParams();
    if (q.alle === true)
        p.set("alle", "true");
    if (q.suche !== undefined && q.suche !== "")
        p.set("suche", q.suche);
    if (q.limit !== undefined)
        p.set("limit", String(q.limit));
    if (q.offset !== undefined)
        p.set("offset", String(q.offset));
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Baut die Abfragezeichenkette beider globaler Listen — an EINER Stelle, weil beide
 *  dieselben fünf Filter tragen und ein Auseinanderlaufen genau die Sorte Fehler wäre, die
 *  erst an der zweiten Maske auffällt. Bauart wie bei `searchProducts`: nur GESETZTE Filter
 *  landen in der Zeichenkette. */
function vorgangsSucheAnhang(q) {
    const p = new URLSearchParams();
    if (q.companyId !== undefined && q.companyId !== "")
        p.set("companyId", q.companyId);
    if (q.status !== undefined && q.status !== "")
        p.set("status", q.status);
    if (q.suche !== undefined && q.suche !== "")
        p.set("suche", q.suche);
    if (q.limit !== undefined)
        p.set("limit", String(q.limit));
    if (q.offset !== undefined)
        p.set("offset", String(q.offset));
    const anhang = p.toString();
    return anhang === "" ? "" : `?${anhang}`;
}
/** Das Pfadsegment zur Vorgangsart. Feste Zeichenketten aus dieser Zuordnung, nie aus einer
 *  Eingabe zusammengesetzt — dieselbe Bauart wie im Repository und in den Routen. */
const VORGANGS_SEGMENT = {
    offer: "offers",
    order: "orders",
    invoice: "invoices",
};
/** Der Pfad zu EINER Rechnung — an einer Stelle für alle sieben Rechnungswege. */
function rechnungsPfad(customerId, invoiceId) {
    return `/api/customers/${encodeURIComponent(customerId)}/invoices/`
        + `${encodeURIComponent(invoiceId)}`;
}
/**
 * Der Rumpf einer Vorschau-Anfrage: `{ dpi }` oder ein LEERES Objekt.
 *
 * Ein leeres Objekt und nicht `undefined`: Die Route hat ein Rumpfschema, und Fastify 5
 * weist ein POST mit gesetztem content-type und leerem Rumpf ab (Befund W1 an den
 * Rendern-Wegen). `{ dpi: undefined }` wäre ausserdem irreführend — in JSON verschwindet
 * der Schlüssel, aber im Code sähe es aus, als würde etwas geschickt.
 */
function vorschauRumpf(dpi) {
    return dpi === undefined ? {} : { dpi };
}
/**
 * Der Bezug als STABILE Zeichenkette — für die Abhängigkeitsliste eines Effekts.
 *
 * Ein Bezug ist ein Objektliteral, und ein Literal im JSX ist bei jedem Zeichnen ein neues:
 * Ein Effekt, der am OBJEKT hängt, lädt nach jedem Zeichnen neu. Gebraucht wird also der
 * INHALT, und zwar in derselben Form an jeder Stelle — der Positions-Editor und die
 * Zahlplan-Tafel hatten die Bildung zweimal abgeschrieben, und zwei Abschriften derselben
 * Regel laufen beim nächsten Bezugsfall auseinander.
 *
 * Er steht HIER und nicht in `web`, weil er zum Typ `Positionsbezug` gehört: Wer den
 * Summentyp erweitert, sieht seinen Schlüssel eine Zeile weiter und vergisst ihn nicht.
 * Die Zeichenkette ist KEIN Pfad und keine Kennung — sie wird nie verschickt, sie
 * unterscheidet nur.
 */
export function bezugsSchluessel(bezug) {
    return bezug.art === "template"
        ? `template:${bezug.vorlageId}`
        : `${bezug.art}:${bezug.customerId}:${bezug.vorgangId}`;
}
/** Der Pfad zu den Positionen — an EINER Stelle für alle sechs Wege und alle VIER
 *  Familien. */
function positionenPfad(bezug) {
    if (bezug.art === "template") {
        // Die Vorlagenfamilie steht unter /api/verwaltung (ADMIN) und trägt nur EINE Kennung.
        return `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(bezug.vorlageId)}`
            + "/positionen";
    }
    return `/api/customers/${encodeURIComponent(bezug.customerId)}/`
        + `${VORGANGS_SEGMENT[bezug.art]}/${encodeURIComponent(bezug.vorgangId)}/positionen`;
}
/** Der Parteien-Pfad eines Vorgangs (Adressen Etappe 3) — an EINER Stelle für alle sechs
 *  Wege (`registriereParteienFamilie` in `routes/belegParteien.ts`). */
function parteienPfad(customerId, art, vorgangId) {
    return `/api/customers/${encodeURIComponent(customerId)}/${VORGANGS_SEGMENT[art]}/`
        + `${encodeURIComponent(vorgangId)}/parteien`;
}
/** Der Zahlplan-Pfad — an EINER Stelle für alle DREI Familien
 *  (`registriereZahlplanFamilie` in `routes/zahlplan.ts`). */
function zahlplanPfad(bezug) {
    if (bezug.art === "template") {
        return `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(bezug.vorlageId)}`
            + "/zahlplan";
    }
    return `/api/customers/${encodeURIComponent(bezug.customerId)}/`
        + `${VORGANGS_SEGMENT[bezug.art]}/${encodeURIComponent(bezug.vorgangId)}/zahlplan`;
}
/** Der Anhangs-Pfad — an EINER Stelle für alle drei Familien
 *  (`registriereAnhangsFamilie` in `routes/anhaenge.ts`). */
function anhangPfad(bezug) {
    if (bezug.art === "template") {
        return `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(bezug.vorlageId)}`
            + "/anhaenge";
    }
    const segment = bezug.art === "offer" ? "offers" : "orders";
    return `/api/customers/${encodeURIComponent(bezug.customerId)}/`
        + `${segment}/${encodeURIComponent(bezug.vorgangId)}/anhaenge`;
}
/** Der Bezug als STABILE Zeichenkette — für die Abhängigkeitsliste eines Effekts
 *  (Muster `bezugsSchluessel`; ein Objektliteral im JSX ist bei jedem Zeichnen ein neues,
 *  und ein Effekt, der am OBJEKT hängt, lädt nach jedem Zeichnen neu). */
export function anhangsSchluessel(bezug) {
    return bezug.art === "template"
        ? `template:${bezug.vorlageId}`
        : `${bezug.art}:${bezug.customerId}:${bezug.vorgangId}`;
}
/** Der Fälligkeiten-Pfad — nur am AUFTRAG (die Planung rechnet nur mit ihm). */
function faelligkeitenPfad(customerId, orderId) {
    return `/api/customers/${encodeURIComponent(customerId)}/orders/`
        + `${encodeURIComponent(orderId)}/faelligkeiten`;
}
/** Die Teilrechnungs-Wege — nur am AUFTRAG (ein Angebot hat keine Rechnungen). */
function auftragsWeg(customerId, orderId, weg) {
    return `/api/customers/${encodeURIComponent(customerId)}/orders/`
        + `${encodeURIComponent(orderId)}/${weg}`;
}
/** Der Listen-/Anlege-Pfad einer Adressfamilie — an EINER Stelle für alle drei Besitzerarten
 *  (`registriereAdressFamilie` in `routes/addresses.ts`). */
function adressWeg(besitzer, ownerId) {
    return `/api/${besitzer}/${encodeURIComponent(ownerId)}/addresses`;
}
function ticketFilter(f = {}) {
    const p = new URLSearchParams();
    if (f.company !== undefined)
        p.set("company", f.company);
    if (f.customer !== undefined)
        p.set("customer", f.customer);
    if (f.assignee !== undefined)
        p.set("assignee", f.assignee);
    if (f.linked === true)
        p.set("linked", "true");
    if (f.archive === true)
        p.set("archive", "true");
    if (f.q !== undefined && f.q !== "")
        p.set("q", f.q);
    if (f.page !== undefined && f.page > 1)
        p.set("page", String(f.page));
    const s = p.toString();
    return s === "" ? "" : `?${s}`;
}
export function createClient(basisUrl = "") {
    return {
        getSession: () => hole(basisUrl, "/api/session"),
        getCompanies: () => hole(basisUrl, "/api/companies"),
        getCustomers: (q = {}) => {
            const p = new URLSearchParams();
            if (q.search !== undefined && q.search !== "")
                p.set("search", q.search);
            if (q.limit !== undefined)
                p.set("limit", String(q.limit));
            if (q.offset !== undefined)
                p.set("offset", String(q.offset));
            const anhang = p.toString();
            return hole(basisUrl, `/api/customers${anhang === "" ? "" : `?${anhang}`}`);
        },
        getCustomer: (id) => hole(basisUrl, `/api/customers/${encodeURIComponent(id)}`),
        updatePreferences: (p) => sende(basisUrl, "/api/preferences", "PUT", p),
        createCustomer: (eingabe) => sendeUndLies(basisUrl, "/api/customers", "POST", eingabe),
        updateCustomer: (id, version, customer) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}`, "PATCH", { version, customer }),
        setCustomerParent: (id, version, parentId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}/parent`, "PATCH", { version, parentId }),
        getKonzern: (id) => hole(basisUrl, `/api/customers/${encodeURIComponent(id)}/konzern`),
        addRelation: (id, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}/relations`, "POST", eingabe),
        findDuplicates: (name) => {
            const p = new URLSearchParams();
            p.set("name", name);
            return hole(basisUrl, `/api/customers/duplicates?${p.toString()}`);
        },
        acquireLock: (ziel, force) => 
        // Der eigentliche Schutz gegen einen zweiten force-Weg steht HIER, nicht im Typ
        // SperrZielKern (siehe dessen Kommentar): force steht bewusst NACH dem Spread von
        // ziel und gewinnt deshalb zur Laufzeit immer — auch dann, wenn ziel über einen
        // vortypisierten Aufrufer trotzdem ein force-Feld mitbringt.
        sendeUndLies(basisUrl, "/api/locks", "POST", { ...ziel, force: force ?? false }),
        refreshLock: (ziel) => sendeUndLies(basisUrl, "/api/locks/refresh", "POST", ziel),
        releaseLock: (ziel) => sende(basisUrl, "/api/locks", "DELETE", ziel),
        lookupEnrichment: (hint) => sendeUndLies(basisUrl, "/api/enrichment/lookup", "POST", hint),
        uploadCustomerLogo: (id, url) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(id)}/logo`, "POST", { url }),
        listSelectableContactRoles: () => hole(basisUrl, "/api/contact-roles"),
        listContacts: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/contacts`),
        createContact: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/contacts`, "POST", eingabe),
        updateContact: (id, version, eingabe) => sendeUndLies(basisUrl, `/api/contacts/${encodeURIComponent(id)}`, "PATCH", { version, ...eingabe }),
        setMainContact: (customerId, contactId) => 
        // Bewusst über sende(): Die Route antwortet mit 204 ohne Rumpf. sendeUndLies() liefe
        // hier in einen UNGUELTIGES_JSON-Fehler, obwohl alles gutgegangen ist.
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/main-contact`, "POST", { contactId }),
        markContactLeft: (id, version, leftOn, nachfolger) => sendeUndLies(basisUrl, `/api/contacts/${encodeURIComponent(id)}/left`, "POST", { version, leftOn, nachfolger: nachfolger ?? null }),
        listNotes: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes`),
        createNote: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes`, "POST", eingabe),
        updateNote: (customerId, noteId, expectedVersion, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes/${encodeURIComponent(noteId)}`, "PUT", { expectedVersion, ...eingabe }),
        deleteNote: (customerId, noteId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/notes/${encodeURIComponent(noteId)}`, "DELETE", undefined),
        listOffers: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers`),
        createOffer: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers`, "POST", eingabe),
        getOffer: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`),
        updateOffer: (customerId, offerId, version, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`, "PUT", { version, ...eingabe }),
        setOfferStatus: (customerId, offerId, version, status) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/status", "PUT", { version, status }),
        neueOfferVersion: (customerId, offerId, vorlageOfferId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/versionen", 
        // Immer ein Objekt, auch ohne Vorlage: Ein POST ganz ohne Rumpf ergäbe an
        // einem Rumpfschema eine 400 über den Inhaltstyp statt über die Sache.
        "POST", { vorlageOfferId }),
        kopiereOffer: (customerId, offerId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/kopie", "POST", {}),
        listOfferVersions: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/${encodeURIComponent(offerId)}`
            + "/versionen"),
        listSalesOrders: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders`),
        createSalesOrder: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders`, "POST", eingabe),
        getSalesOrder: (customerId, orderId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}`),
        updateSalesOrder: (customerId, orderId, version, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}`, "PUT", { version, ...eingabe }),
        setSalesOrderStatus: (customerId, orderId, version, status) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/${encodeURIComponent(orderId)}`
            + "/status", "PUT", { version, status }),
        listInvoices: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/invoices`),
        getInvoice: (customerId, invoiceId) => hole(basisUrl, rechnungsPfad(customerId, invoiceId)),
        updateInvoice: (customerId, invoiceId, version, eingabe) => sendeUndLies(basisUrl, rechnungsPfad(customerId, invoiceId), "PATCH", { version, ...eingabe }),
        createInvoiceFromOrder: (customerId, orderId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/`
            + `${encodeURIComponent(orderId)}/rechnung-erzeugen`, 
        // OHNE Rumpf: Die Route braucht nichts ausser den beiden Kennungen im Pfad — alles
        // Weitere steht am Auftrag. Ein leeres Objekt wäre ein Rumpf, den niemand liest.
        "POST", undefined),
        issueInvoice: (customerId, invoiceId, version, invoiceDate = null) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/festschreiben`, "POST", 
        // `invoiceDate` steht IMMER im Rumpf, auch als null: Der Vertrag unterscheidet
        // „heute" (null) nicht von „weggelassen" — beides bedeutet dasselbe —, aber ein
        // ausdrücklicher Wert liest sich im Netzwerkprotokoll besser als ein fehlendes Feld
        // (dieselbe Entscheidung wie bei `beiSortIndex`).
        { version, invoiceDate }),
        cancelInvoice: (customerId, invoiceId, version) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/stornieren`, "POST", { version }),
        markInvoicePaid: (customerId, invoiceId, version, paidAt = null) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/als-bezahlt`, "POST", 
        // `paidAt` steht IMMER im Rumpf, auch als null — dieselbe Entscheidung und
        // dieselbe Begründung wie bei `invoiceDate` in `issueInvoice`.
        { version, paidAt }),
        unmarkInvoicePaid: (customerId, invoiceId, version) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/bezahlt-zuruecknehmen`, "POST", { version }),
        createInvoiceKorrektur: async (customerId, invoiceId) => {
            // 201 = eben angelegt, 200 = es gab schon eine (siehe `sendeUndLiesMitCode`).
            const { status, daten } = await sendeUndLiesMitCode(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/korrektur-erzeugen`, "POST", undefined);
            return { korrektur: daten, bestand: status === 200 };
        },
        invoiceRenderModel: (customerId, invoiceId) => hole(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/rendermodell`),
        renderInvoice: (customerId, invoiceId, profil = "PDF_A3") => sendeBinaer(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/rendern`
            // Getippt und aus der erzeugten Aufzählung — hier kann nichts anderes stehen;
            // die Kodierung bleibt trotzdem, weil ein Pfadbau ohne sie zum Muster würde.
            + `?profil=${encodeURIComponent(profil)}`),
        vorschauInvoice: (customerId, invoiceId, dpi) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/vorschau`, "POST", vorschauRumpf(dpi)),
        getInvoiceAblage: (customerId, invoiceId) => hole(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/ablage`),
        createInvoiceAblage: (customerId, invoiceId) => sendeUndLies(basisUrl, `${rechnungsPfad(customerId, invoiceId)}/in-akte-ablegen`, 
        // OHNE Rumpf, wie `createInvoiceFromOrder`: Die Route braucht nichts ausser den
        // beiden Kennungen im Pfad — was abgelegt wird, steht an der Rechnung.
        "POST", undefined),
        offerRenderModel: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/rendermodell`),
        renderOffer: (customerId, offerId) => sendeBinaer(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/rendern`),
        vorschauOffer: (customerId, offerId, dpi) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/vorschau`, "POST", vorschauRumpf(dpi)),
        getOfferAblage: (customerId, offerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/ablage`),
        createOfferAblage: (customerId, offerId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/in-akte-ablegen`, 
        // OHNE Rumpf, wie `createInvoiceAblage`: Die Route braucht nichts ausser den
        // beiden Kennungen im Pfad — was abgelegt wird, steht am Angebot.
        "POST", undefined),
        orderRenderModel: (customerId, orderId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/`
            + `${encodeURIComponent(orderId)}/rendermodell`),
        renderOrder: (customerId, orderId) => sendeBinaer(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/`
            + `${encodeURIComponent(orderId)}/rendern`),
        vorschauOrder: (customerId, orderId, dpi) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/orders/`
            + `${encodeURIComponent(orderId)}/vorschau`, "POST", vorschauRumpf(dpi)),
        listItems: (bezug) => hole(basisUrl, positionenPfad(bezug)),
        createItem: (bezug, version, eingabe, beiSortIndex = null) => sendeUndLies(basisUrl, positionenPfad(bezug), "POST", 
        // `beiSortIndex` steht IMMER im Rumpf, auch als null: Der Vertrag unterscheidet
        // „ans Ende" (null) nicht von „weggelassen" — beides bedeutet dasselbe —, aber ein
        // ausdrücklicher Wert liest sich im Netzwerkprotokoll besser als ein fehlendes Feld.
        { version, beiSortIndex, ...eingabe }),
        updateItem: (bezug, itemId, version, eingabe) => sendeUndLies(basisUrl, `${positionenPfad(bezug)}/${encodeURIComponent(itemId)}`, "PATCH", { version, ...eingabe }),
        deleteItem: (bezug, itemId, version) => 
        // sendeUndLies(), nicht sende(): Die Antwort trägt den neuen Stand (kein 204). Der
        // Rumpf bleibt dabei LEER (`undefined` → JSON.stringify liefert undefined → fetch
        // sendet keinen) und die Version geht als Abfrageparameter hinaus — Zwischenserver
        // dürfen den Rumpf einer DELETE-Anfrage verwerfen (Ruling P3B1B-F).
        sendeUndLies(basisUrl, `${positionenPfad(bezug)}/${encodeURIComponent(itemId)}`
            + `?version=${encodeURIComponent(String(version))}`, "DELETE", undefined),
        reorderItems: (bezug, version, geordneteIds) => sendeUndLies(basisUrl, `${positionenPfad(bezug)}/reihenfolge`, "POST", { version, geordneteIds }),
        resetItem: (bezug, itemId, version) => sendeUndLies(basisUrl, `${positionenPfad(bezug)}/${encodeURIComponent(itemId)}/reset`, "POST", { version }),
        applyTemplate: (customerId, offerId, version, vorlageId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/offers/`
            + `${encodeURIComponent(offerId)}/vorlage-anwenden`, "POST", { version, vorlageId }),
        listPlaceholders: () => hole(basisUrl, "/api/platzhalter"),
        searchProducts: (q = {}) => {
            // Bauart wie listProducts: nur gesetzte Filter landen in der Abfragezeichenkette. Ein
            // leerer Text wäre sonst ein Filter auf die leere Zeichenkette und träfe nichts.
            // `nurAktive` fehlt hier absichtlich — der Server brennt es ein (Ruling P3B1B-G) und
            // wiese ein mitgeschicktes Feld mit 400 ab.
            const p = new URLSearchParams();
            if (q.companyId !== undefined && q.companyId !== "")
                p.set("companyId", q.companyId);
            if (q.category !== undefined && q.category !== "")
                p.set("category", q.category);
            if (q.suche !== undefined && q.suche !== "")
                p.set("suche", q.suche);
            if (q.limit !== undefined)
                p.set("limit", String(q.limit));
            if (q.offset !== undefined)
                p.set("offset", String(q.offset));
            const anhang = p.toString();
            return hole(basisUrl, `/api/artikel${anhang === "" ? "" : `?${anhang}`}`);
        },
        listActiveTaxRates: () => hole(basisUrl, "/api/steuersaetze"),
        listTemplates: (companyId) => 
        // `companyId` ist PFLICHT (anders als bei searchProducts) und steht deshalb immer in
        // der Abfrage: Eine Vorlagenliste ohne Unternehmen wäre eine, aus der man nichts
        // anwenden darf — der Server weist sie mit 400 ab.
        hole(basisUrl, `/api/angebotsvorlagen?companyId=${encodeURIComponent(companyId)}`),
        searchInvoices: (q = {}) => hole(basisUrl, `/api/invoices${rechnungsSucheAnhang(q)}`),
        searchOffers: (q = {}) => hole(basisUrl, `/api/offers${vorgangsSucheAnhang(q)}`),
        searchOrders: (q = {}) => hole(basisUrl, `/api/orders${vorgangsSucheAnhang(q)}`),
        getOfferById: (offerId) => hole(basisUrl, `/api/offers/${encodeURIComponent(offerId)}`),
        getOrderById: (orderId) => hole(basisUrl, `/api/orders/${encodeURIComponent(orderId)}`),
        interfaceStatus: () => hole(basisUrl, "/api/schnittstellen/status"),
        aircallStatus: () => hole(basisUrl, "/api/aircall/status"),
        aircallKlaerliste: (q = {}) => hole(basisUrl, `/api/aircall/klaerliste${klaerlisteAnhang(q)}`),
        aircallZuordnen: (eingabe) => sendeUndLies(basisUrl, "/api/aircall/klaerliste/zuordnen", "POST", eingabe),
        aircallIgnorieren: (nummer) => sendeUndLies(basisUrl, "/api/aircall/klaerliste/ignorieren", "POST", { nummer }),
        listImportantNotes: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes`),
        createImportantNote: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes`, "POST", eingabe),
        updateImportantNote: (customerId, noteId, version, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes/`
            + `${encodeURIComponent(noteId)}`, "PUT", { version, ...eingabe }),
        deleteImportantNote: (customerId, noteId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/important-notes/`
            + `${encodeURIComponent(noteId)}`, "DELETE", undefined),
        listOrderRuntimeWarnings: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/order-runtime-warnings`),
        listExternalReferences: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references`),
        createExternalReference: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references`, "POST", eingabe),
        deleteExternalReference: (customerId, referenceId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references/`
            + `${encodeURIComponent(referenceId)}`, "DELETE", undefined),
        getAircallAccount: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account`),
        setzeAircallAccount: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account`, "PUT", eingabe),
        loescheAircallAccount: (customerId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/aircall-account`, "DELETE", undefined),
        monitorKarte: (customerId, referenceId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/external-references/`
            + `${encodeURIComponent(referenceId)}/monitor`),
        listAddresses: (besitzer, ownerId) => hole(basisUrl, adressWeg(besitzer, ownerId)),
        createAddress: (besitzer, ownerId, eingabe) => sendeUndLies(basisUrl, adressWeg(besitzer, ownerId), "POST", eingabe),
        updateAddress: (besitzer, ownerId, addressId, eingabe) => sendeUndLies(basisUrl, `${adressWeg(besitzer, ownerId)}/${encodeURIComponent(addressId)}`, "PATCH", eingabe),
        setPrimaryAddress: (besitzer, ownerId, addressId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `${adressWeg(besitzer, ownerId)}/${encodeURIComponent(addressId)}/primary`, "POST", undefined),
        archiveAddress: (besitzer, ownerId, addressId) => sende(basisUrl, `${adressWeg(besitzer, ownerId)}/${encodeURIComponent(addressId)}`, "DELETE", undefined),
        getBelegParteien: (art, customerId, vorgangId) => hole(basisUrl, parteienPfad(customerId, art, vorgangId)),
        setBelegParteien: (art, customerId, vorgangId, eingabe) => sendeUndLies(basisUrl, parteienPfad(customerId, art, vorgangId), "PUT", eingabe),
        getZahlplan: (bezug) => hole(basisUrl, zahlplanPfad(bezug)),
        setZahlplan: (bezug, eingabe) => sendeUndLies(basisUrl, zahlplanPfad(bezug), "PUT", eingabe),
        getFaelligkeiten: (customerId, orderId) => hole(basisUrl, faelligkeitenPfad(customerId, orderId)),
        berechneFaelligkeiten: (customerId, orderId) => sendeUndLies(basisUrl, `${faelligkeitenPfad(customerId, orderId)}/neu-berechnen`, "POST", {}),
        getAbrechnungsquellen: (customerId, orderId) => hole(basisUrl, auftragsWeg(customerId, orderId, "abrechnungsquellen")),
        setzeAbrechnungsquelle: (customerId, orderId, source, eingabe) => sendeUndLies(basisUrl, `${auftragsWeg(customerId, orderId, "abrechnungsquellen")}/${encodeURIComponent(source)}`, "PUT", eingabe),
        loeseAbrechnungsquelle: (customerId, orderId, source, version) => sendeUndLies(basisUrl, `${auftragsWeg(customerId, orderId, "abrechnungsquellen")}/${encodeURIComponent(source)}`
            + `?version=${String(version)}`, "DELETE", undefined),
        getAnhaenge: (bezug) => hole(basisUrl, anhangPfad(bezug)),
        anhangAnhaengen: (bezug, version, textBlockId) => sendeUndLies(basisUrl, anhangPfad(bezug), "POST", { version, textBlockId }),
        anhangEntfernen: (bezug, anhangId, version) => sendeUndLies(basisUrl, `${anhangPfad(bezug)}/${encodeURIComponent(anhangId)}`
            + `?version=${String(version)}`, "DELETE", undefined),
        anhaengeOrdnen: (bezug, version, geordneteIds) => sendeUndLies(basisUrl, `${anhangPfad(bezug)}/reihenfolge`, "PUT", { version, geordneteIds }),
        anhangSichtbarkeit: (customerId, orderId, anhangId, version, hidden) => sendeUndLies(basisUrl, `${anhangPfad({ art: "order", customerId, vorgangId: orderId })}`
            + `/${encodeURIComponent(anhangId)}`, "PATCH", { version, hidden }),
        getRechnungsvorschlag: (customerId, orderId, stichtag) => hole(basisUrl, auftragsWeg(customerId, orderId, "rechnungsvorschlag")
            + (stichtag === undefined ? "" : `?stichtag=${encodeURIComponent(stichtag)}`)),
        erzeugeRechnungAusVorschlag: (customerId, orderId, eingabe) => sendeUndLies(basisUrl, auftragsWeg(customerId, orderId, "rechnungsvorschlag"), "POST", eingabe),
        getAbrechnung: (customerId, orderId, periodStart) => hole(basisUrl, auftragsWeg(customerId, orderId, "abrechnung")
            + (periodStart === undefined
                ? "" : `?jahr=${encodeURIComponent(periodStart)}`)),
        getVolumen: (customerId, orderId, jahr) => hole(basisUrl, auftragsWeg(customerId, orderId, "volumen")
            + (jahr === undefined ? "" : `?jahr=${jahr}`)),
        uploadFile: (customerId, datei, optionen = {}) => {
            const form = new FormData();
            // folderId/fileTypeId VOR dem Datei-Teil anhängen — nur so liegen sie serverseitig an
            // daten.fields (der Handler liest sie vor dem Stream).
            if (optionen.folderId !== undefined)
                form.append("folderId", optionen.folderId);
            if (optionen.fileTypeId !== undefined)
                form.append("fileTypeId", optionen.fileTypeId);
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files`, form);
        },
        downloadFile: (customerId, fileId) => holeBinaer(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`),
        listFiles: (customerId, filter = {}) => {
            const p = new URLSearchParams();
            // null => "null" (Wurzel/unkategorisiert), ein Wert => der Wert, undefined => weglassen.
            if (filter.folderId !== undefined)
                p.set("folderId", filter.folderId ?? "null");
            if (filter.fileTypeId !== undefined)
                p.set("fileTypeId", filter.fileTypeId ?? "null");
            const anhang = p.toString();
            return hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files${anhang === "" ? "" : `?${anhang}`}`);
        },
        renameFile: (customerId, fileId, name) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "PATCH", { originalName: name }),
        moveFile: (customerId, fileId, folderId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "PATCH", { folderId }),
        setFileType: (customerId, fileId, fileTypeId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "PATCH", { fileTypeId }),
        deleteFile: (customerId, fileId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/files/${encodeURIComponent(fileId)}`, "DELETE", undefined),
        listFolders: (customerId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders`),
        createFolder: (customerId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders`, "POST", eingabe),
        renameFolder: (customerId, folderId, name) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders/${encodeURIComponent(folderId)}`, "PATCH", { name }),
        moveFolder: (customerId, folderId, newParentId) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders/${encodeURIComponent(folderId)}`, "PATCH", { parentId: newParentId }),
        deleteFolder: (customerId, folderId) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/folders/${encodeURIComponent(folderId)}`, "DELETE", undefined),
        listSelectablePaymentTerms: () => hole(basisUrl, "/api/payment-terms"),
        listLaender: () => hole(basisUrl, "/api/laender"),
        getRelationTerms: (relationId) => hole(basisUrl, `/api/relations/${encodeURIComponent(relationId)}/terms`),
        setRelationTerms: (relationId, version, terms) => sendeUndLies(basisUrl, `/api/relations/${encodeURIComponent(relationId)}/terms`, "PATCH", { version, terms }),
        listUsers: () => hole(basisUrl, "/api/verwaltung/users"),
        setUserRole: (id, version, role, homeCompanyId) => sendeUndLies(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}`, "PATCH", { version, role, homeCompanyId }),
        setUserSignature: (id, datei) => {
            const form = new FormData();
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/unterschrift`, form, "PUT");
        },
        deleteUserSignature: (id) => sendeUndLies(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/unterschrift`, "DELETE", undefined),
        userSignatureUrl: (id, stempel) => `${basisUrl}/api/verwaltung/users/${encodeURIComponent(id)}/unterschrift`
            + (stempel === undefined ? "" : `?v=${stempel}`),
        listUserJobTitles: (id) => hole(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/funktionen`),
        setUserJobTitles: (id, items) => sendeUndLies(basisUrl, `/api/verwaltung/users/${encodeURIComponent(id)}/funktionen`, "PUT", { items }),
        listLayouts: () => hole(basisUrl, "/api/verwaltung/layouts"),
        getLayout: (id) => hole(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}`),
        saveLayoutDraft: (id, version, entwurf) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/entwurf`, "PUT", { version, entwurf }),
        previewLayout: (id, anfrage) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/vorschau`, "POST", anfrage),
        listLayoutRealDocuments: (id) => hole(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/echte-belege`),
        getLayoutCapabilities: () => hole(basisUrl, "/api/verwaltung/layouts/faehigkeiten"),
        createLayoutGraphic: (id, felder, datei) => {
            const form = new FormData();
            form.append("felder", JSON.stringify(felder));
            if (datei !== null)
                form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken`, form);
        },
        updateLayoutGraphic: (id, gid, felder) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}`
            + `/grafiken/${encodeURIComponent(gid)}`, "PUT", felder),
        deleteLayoutGraphic: (id, gid, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}`
            + `/grafiken/${encodeURIComponent(gid)}?version=${version}`, "DELETE", undefined),
        reorderLayoutGraphics: (id, version, geordneteIds) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken/reihenfolge`, "PUT", { version, geordneteIds }),
        publishLayout: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/freigabe`, "POST", { version }),
        discardLayout: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/verwerfen`, "POST", { version }),
        adoptLayoutStore: (id, version) => sendeUndLies(basisUrl, `/api/verwaltung/layouts/${encodeURIComponent(id)}/ablage-uebernehmen`, "POST", { version }),
        layoutGraphicUrl: (id, gid, stempel) => `${basisUrl}/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken/`
            + `${encodeURIComponent(gid)}/svg${stempel === undefined ? "" : `?v=${stempel}`}`,
        layoutGraphicImageUrl: (id, gid, stempel) => `${basisUrl}/api/verwaltung/layouts/${encodeURIComponent(id)}/grafiken/`
            + `${encodeURIComponent(gid)}/bild${stempel === undefined ? "" : `?v=${stempel}`}`,
        listNumberRanges: () => hole(basisUrl, "/api/verwaltung/number-ranges"),
        updateNumberRangePattern: (id, expectedUpdatedAt, pattern) => sendeUndLies(basisUrl, `/api/verwaltung/number-ranges/${encodeURIComponent(id)}`, "PATCH", { expectedUpdatedAt, pattern }),
        setNumberRangeStart: (id, expectedUpdatedAt, startValue) => 
        // Nur diese beiden Felder — der Startwert-Zweig des Schemas verträgt kein `pattern`
        // daneben (dann 400).
        sendeUndLies(basisUrl, `/api/verwaltung/number-ranges/${encodeURIComponent(id)}`, "PATCH", { expectedUpdatedAt, startValue }),
        listPaymentTermsVerwaltung: () => hole(basisUrl, "/api/verwaltung/payment-terms"),
        createPaymentTerm: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/payment-terms", "POST", eingabe),
        updatePaymentTerm: (id, version, eingabe) => sendeUndLies(basisUrl, `/api/verwaltung/payment-terms/${encodeURIComponent(id)}`, "PATCH", { version, ...eingabe }),
        archivePaymentTerm: (id, version) => 
        // Nur diese beiden Felder — der Archivierzweig des Schemas verträgt kein weiteres
        // (dann 400), und die Antwort kommt als 204 ohne Rumpf.
        sende(basisUrl, `/api/verwaltung/payment-terms/${encodeURIComponent(id)}`, "PATCH", { version, archiviert: true }),
        listContactRoles: () => hole(basisUrl, "/api/verwaltung/contact-roles"),
        createContactRole: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/contact-roles", "POST", eingabe),
        updateContactRole: (id, version, eingabe) => sendeUndLies(basisUrl, `/api/verwaltung/contact-roles/${encodeURIComponent(id)}`, "PATCH", { version, ...eingabe }),
        listCompaniesMaster: () => hole(basisUrl, "/api/verwaltung/unternehmen"),
        updateCompanyMaster: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist der volle Sieben-Feld-Satz der
        // Stammdaten, wahlweise um name/colorHex ergänzt.
        sendeUndLies(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(id)}`, "PATCH", eingabe),
        createCompany: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/unternehmen", "POST", eingabe),
        setCompanyActive: (id, aktiv) => sendeUndLies(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(id)}/stilllegen`, "POST", { aktiv }),
        uploadCompanyLogo: (id, variante, datei) => {
            const form = new FormData();
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, verwaltungsLogoPfad(id, variante), form);
        },
        deleteCompanyLogo: (id, variante) => sendeUndLies(basisUrl, verwaltungsLogoPfad(id, variante), "DELETE", undefined),
        listFileTypes: () => hole(basisUrl, "/api/verwaltung/dateitypen"),
        createFileType: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/dateitypen", "POST", eingabe),
        updateFileType: (id, eingabe) => 
        // Ohne `version` (Ruling P4-B): der Rumpf ist genau der volle Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/dateitypen/${encodeURIComponent(id)}`, "PATCH", eingabe),
        setFileTypeActive: (id, active) => 
        // Der Toggle-Rumpf trägt AUSSER `aktiv` kein Feld (sonst 400) und antwortet mit dem
        // geänderten Satz — daher sendeUndLies, nicht sende.
        sendeUndLies(basisUrl, `/api/verwaltung/dateitypen/${encodeURIComponent(id)}`, "PATCH", { aktiv: active }),
        listTaxRates: () => hole(basisUrl, "/api/verwaltung/steuersaetze"),
        createTaxRate: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/steuersaetze", "POST", eingabe),
        updateTaxRate: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der volle Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/steuersaetze/${encodeURIComponent(id)}`, "PATCH", eingabe),
        setTaxRateActive: (id, active) => sendeUndLies(basisUrl, `/api/verwaltung/steuersaetze/${encodeURIComponent(id)}`, "PATCH", { aktiv: active }),
        listProducts: (q = {}) => {
            // Bauart wie getCustomers: nur gesetzte Filter landen in der Abfragezeichenkette.
            // Ein leerer Text wäre sonst ein Filter auf die leere Zeichenkette und träfe nichts.
            const p = new URLSearchParams();
            if (q.companyId !== undefined && q.companyId !== "")
                p.set("companyId", q.companyId);
            if (q.category !== undefined && q.category !== "")
                p.set("category", q.category);
            if (q.suche !== undefined && q.suche !== "")
                p.set("suche", q.suche);
            if (q.nurAktive === true)
                p.set("nurAktive", "true");
            if (q.limit !== undefined)
                p.set("limit", String(q.limit));
            if (q.offset !== undefined)
                p.set("offset", String(q.offset));
            const anhang = p.toString();
            return hole(basisUrl, `/api/verwaltung/artikel${anhang === "" ? "" : `?${anhang}`}`);
        },
        createProduct: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/artikel", "POST", eingabe),
        updateProduct: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der volle Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/artikel/${encodeURIComponent(id)}`, "PATCH", eingabe),
        setProductActive: (id, active) => sendeUndLies(basisUrl, `/api/verwaltung/artikel/${encodeURIComponent(id)}`, "PATCH", { aktiv: active }),
        listTextBlocks: (q = {}) => {
            // Bauart wie listProducts: Ein weggelassener (oder leerer) Filter erscheint gar nicht
            // erst in der Abfrage — er wäre sonst ein Filter auf die leere Zeichenkette.
            const anhang = q.companyId !== undefined && q.companyId !== ""
                ? `?companyId=${encodeURIComponent(q.companyId)}` : "";
            return hole(basisUrl, `/api/verwaltung/textbausteine${anhang}`);
        },
        createTextBlock: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/textbausteine", "POST", eingabe),
        updateTextBlock: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der volle Feldsatz — samt
        // `companyId`, denn die Zugehörigkeit ist hier änderbar.
        sendeUndLies(basisUrl, `/api/verwaltung/textbausteine/${encodeURIComponent(id)}`, "PATCH", eingabe),
        deleteTextBlock: (id) => 
        // Antwortet mit 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/verwaltung/textbausteine/${encodeURIComponent(id)}`, "DELETE", undefined),
        listOfferTemplates: (q = {}) => {
            // Bauart wie listTextBlocks: Ein weggelassener (oder leerer) Filter erscheint gar
            // nicht erst in der Abfrage — er wäre sonst ein Filter auf die leere Zeichenkette.
            const anhang = q.companyId !== undefined && q.companyId !== ""
                ? `?companyId=${encodeURIComponent(q.companyId)}` : "";
            return hole(basisUrl, `/api/verwaltung/angebotsvorlagen${anhang}`);
        },
        getOfferTemplate: (id) => hole(basisUrl, `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(id)}`),
        createOfferTemplate: (eingabe) => sendeUndLies(basisUrl, "/api/verwaltung/angebotsvorlagen", "POST", eingabe),
        updateOfferTemplate: (id, eingabe) => 
        // Ohne `version` (last-write-wins): der Rumpf ist genau der Kopf-Feldsatz.
        sendeUndLies(basisUrl, `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(id)}`, "PATCH", eingabe),
        deleteOfferTemplate: (id) => 
        // 204 ohne Rumpf — deshalb sende(), nicht sendeUndLies().
        sende(basisUrl, `/api/verwaltung/angebotsvorlagen/${encodeURIComponent(id)}`, "DELETE", undefined),
        getDatevKopplung: (companyId) => hole(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(companyId)}/datev`),
        setDatevKopplung: (companyId, eingabe) => sendeUndLies(basisUrl, `/api/verwaltung/unternehmen/${encodeURIComponent(companyId)}/datev`, "PUT", eingabe),
        listDatevKopplungen: () => hole(basisUrl, "/api/datev/unternehmen"),
        getDatevAbgleich: (companyId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren`),
        einlesenDatevDebitoren: (companyId, datei, standAt) => {
            const form = new FormData();
            // `standAt` VOR dem Datei-Teil (Muster `uploadFile`): Der Handler liest die Felder,
            // bevor er den Datei-Stream öffnet.
            if (standAt !== undefined)
                form.append("standAt", standAt);
            form.append("datei", datei, datei.name);
            return sendeFormular(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/einlesen`, form);
        },
        verwerfeDatevDebitorenLauf: (companyId, importId) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/laeufe/`
            + encodeURIComponent(importId), "DELETE", undefined),
        legeKundeAusDatevDebitorAn: (companyId, debtorNumber) => sendeUndLies(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/kunde-anlegen`, "POST", { debtorNumber }),
        ignoriereDatevAbgleichZeile: (companyId, ziel, optionen = {}) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/ignorieren`, "POST", { ...ziel, ...optionen }),
        exportiereDatevDebitoren: (companyId) => holeBinaer(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/debitoren/export`),
        getDatevAusgang: (companyId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang`),
        erzeugeDatevStapel: (companyId, von, bis) => sendeUndLies(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang/stapel`, "POST", { von, bis }),
        holeDatevStapelPaket: (companyId, batchId) => holeBinaer(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang/stapel/`
            + `${encodeURIComponent(batchId)}/paket`),
        bestaetigeDatevStapel: (companyId, batchId) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/ausgang/stapel/`
            + `${encodeURIComponent(batchId)}/bestaetigen`, "POST", undefined),
        getDatevRueckweg: (companyId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg`),
        einlesenDatevBuchungen: (companyId, datei, von, bis, standAt) => {
            const form = new FormData();
            form.append("von", von);
            form.append("bis", bis);
            if (standAt !== undefined)
                form.append("standAt", standAt);
            form.append("datei", datei, datei.name);
            return sendeFormular(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/einlesen`, form);
        },
        verwerfeDatevBuchungsLauf: (companyId, importId) => sende(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/laeufe/`
            + encodeURIComponent(importId), "DELETE", undefined),
        getDatevZahlungsliste: (companyId, importId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/laeufe/`
            + `${encodeURIComponent(importId)}/zahlungen`),
        datevZahlungUebernehmen: (companyId, bookingId, zuruecknehmen) => sendeUndLies(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/zahlungen/`
            + encodeURIComponent(bookingId), "POST", zuruecknehmen === undefined ? {} : { zuruecknehmen }),
        getDatevKonten: (companyId, importId) => hole(basisUrl, `/api/datev/${encodeURIComponent(companyId)}/rueckweg/laeufe/`
            + `${encodeURIComponent(importId)}/konten`),
        getDatevStandZuRechnung: (customerId, invoiceId) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/invoices/`
            + `${encodeURIComponent(invoiceId)}/datev`),
        setDebitorennummer: (customerId, relationId, eingabe) => sendeUndLies(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/relations/`
            + `${encodeURIComponent(relationId)}/debitor`, "POST", eingabe),
        // Das Board kennt nur die vier Filter (Review 9) — Archiv, Suche und Seite fallen weg,
        // statt am Board-Schema mit 400 zu scheitern.
        getTicketBoard: (f = {}) => {
            const { archive: _archiv, q: _suche, page: _seite, ...board } = f;
            return hole(basisUrl, `/api/tickets/board${ticketFilter(board)}`);
        },
        listTickets: (f) => hole(basisUrl, `/api/tickets${ticketFilter(f)}`),
        listCustomerTickets: (customerId, archiv) => hole(basisUrl, `/api/customers/${encodeURIComponent(customerId)}/tickets${archiv === true ? "?archive=true" : ""}`),
        createTicket: (e) => sendeUndLies(basisUrl, "/api/tickets", "POST", e),
        getTicket: (id) => hole(basisUrl, `/api/tickets/${encodeURIComponent(id)}`),
        updateTicket: (id, version, patch) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}`, "PATCH", { version, ...patch }),
        ablageTicket: (id, ablage) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/ablage`, "POST", ablage),
        listTicketColumns: () => hole(basisUrl, "/api/ticket-columns"),
        createTicketColumn: (e) => sendeUndLies(basisUrl, "/api/ticket-columns", "POST", e),
        updateTicketColumn: (id, version, e) => sendeUndLies(basisUrl, `/api/ticket-columns/${encodeURIComponent(id)}`, "PATCH", { version, ...e }),
        deleteTicketColumn: (id) => sende(basisUrl, `/api/ticket-columns/${encodeURIComponent(id)}`, "DELETE", undefined),
        orderTicketColumns: (ids) => sendeUndLies(basisUrl, "/api/ticket-columns/order", "POST", { ids }),
        listTicketAssignees: () => hole(basisUrl, "/api/tickets/zustaendige"),
        addTicketCustomer: (t, customerId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/customers`, "POST", { customerId }),
        previewRemoveTicketCustomer: (t, c) => hole(basisUrl, `/api/tickets/${encodeURIComponent(t)}/customers/${encodeURIComponent(c)}/vorschau`),
        removeTicketCustomer: (t, c) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/customers/${encodeURIComponent(c)}`, "DELETE", undefined),
        addTicketContact: (t, contactId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/contacts`, "POST", { contactId }),
        removeTicketContact: (t, c) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/contacts/${encodeURIComponent(c)}`, "DELETE", undefined),
        addTicketSubtask: (t, title) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/subtasks`, "POST", { title }),
        updateTicketSubtask: (t, s, version, patch) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/subtasks/${encodeURIComponent(s)}`, "PATCH", { version, ...patch }),
        deleteTicketSubtask: (t, s) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/subtasks/${encodeURIComponent(s)}`, "DELETE", undefined),
        orderTicketSubtasks: (t, ids) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/subtasks/order`, "POST", { ids }),
        addTicketComment: (t, contentMarkdown) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/comments`, "POST", { contentMarkdown }),
        updateTicketComment: (t, k, version, contentMarkdown) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/comments/${encodeURIComponent(k)}`, "PATCH", { version, contentMarkdown }),
        deleteTicketComment: (t, k) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/comments/${encodeURIComponent(k)}`, "DELETE", undefined),
        addTicketReference: (t, kind, targetId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/references`, "POST", { kind, targetId }),
        removeTicketReference: (t, r) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/references/${encodeURIComponent(r)}`, "DELETE", undefined),
        searchTicketReferenceCandidates: (t, q) => hole(basisUrl, `/api/tickets/${encodeURIComponent(t)}/reference-candidates?q=${encodeURIComponent(q)}`),
        uploadTicketAttachment: (t, datei) => {
            const form = new FormData();
            form.append("file", datei, datei.name);
            return sendeFormular(basisUrl, `/api/tickets/${encodeURIComponent(t)}/attachments`, form);
        },
        deleteTicketAttachment: (t, a) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/attachments/${encodeURIComponent(a)}`, "DELETE", undefined),
        ticketAttachmentUrl: (t, a) => `${basisUrl}/api/tickets/${encodeURIComponent(t)}/attachments/${encodeURIComponent(a)}`,
        addTicketLink: (t, e) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/links`, "POST", e),
        removeTicketLink: (t, l) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/links/${encodeURIComponent(l)}`, "DELETE", undefined),
        refreshTicketLinks: (t) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(t)}/links/refresh`, "POST", undefined),
        listTicketLinkInstanzen: (companyId) => hole(basisUrl, `/api/ticket-links/instanzen${companyId === undefined ? "" : `?company=${encodeURIComponent(companyId)}`}`),
        searchTicketLinks: (system, instance, q) => hole(basisUrl, `/api/ticket-links/search?system=${system}&instance=${encodeURIComponent(instance)}&q=${encodeURIComponent(q)}`),
        watchTicket: (id) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watch`, "PUT", undefined),
        unwatchTicket: (id) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watch`, "DELETE", undefined),
        addWatcher: (id, userId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watchers`, "POST", { userId }),
        removeWatcher: (id, userId) => sendeUndLies(basisUrl, `/api/tickets/${encodeURIComponent(id)}/watchers/${encodeURIComponent(userId)}`, "DELETE", undefined),
        listNotifications: (o = {}) => {
            const p = new URLSearchParams();
            if (o.nurUngelesen === true)
                p.set("nurUngelesen", "true");
            if (o.limit !== undefined)
                p.set("limit", String(o.limit));
            const anhang = p.toString();
            return hole(basisUrl, `/api/notifications${anhang === "" ? "" : `?${anhang}`}`);
        },
        countNotifications: () => hole(basisUrl, "/api/notifications/count"),
        markNotificationsRead: (z) => sendeUndLies(basisUrl, "/api/notifications/read", "POST", z),
        getNotificationSettings: () => hole(basisUrl, "/api/notification-settings"),
        putNotificationSettings: (e) => sendeUndLies(basisUrl, "/api/notification-settings", "PUT", e),
        getPushKey: () => hole(basisUrl, "/api/push/key"),
        listPushDevices: () => hole(basisUrl, "/api/push/subscriptions"),
        registerPush: (abo) => sendeUndLies(basisUrl, "/api/push/subscriptions", "POST", abo),
        removePushDevice: (id) => sende(basisUrl, `/api/push/subscriptions/${encodeURIComponent(id)}`, "DELETE", undefined),
        unregisterPush: (endpoint) => sende(basisUrl, "/api/push/subscriptions", "DELETE", { endpoint }),
    };
}
//# sourceMappingURL=client.js.map